/**
 * Routing, and what happens before there is a session.
 *
 * There is no client-side authorization here. A route renders, the screen asks
 * Nexus, and Nexus decides — a refusal comes back as a refusal and is rendered
 * as one. What the capability report changes is only what the application
 * *offers*: a destination nobody may reach is not in the navigation, and a
 * screen somebody reaches anyway still asks and is still told no.
 */

import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { NexusError } from "./api/errors";
import type { Resource } from "./api/useResource";
import { AppShell } from "./components/AppShell";
import { WorkspaceHub } from "./components/WorkspaceHub";
import { workspaceByKey } from "./components/navigation";
import { Failed, Loading } from "./components/states";
import { SessionProvider } from "./session/SessionProvider";
import { AuditCorrelation, AuditExplorer } from "./screens/AuditExplorer";
import { Authorization } from "./screens/Authorization";
import { Brains } from "./screens/Brains";
import { ContextContracts } from "./screens/ContextContracts";
import { Memory } from "./screens/Memory";
import { Consent } from "./screens/Consent";
import { ConnectedSystems } from "./screens/ConnectedSystems";
import { Entities } from "./screens/Entities";
import { EnterpriseGraph } from "./screens/EnterpriseGraph";
import { EventBackbone } from "./screens/EventBackbone";
import { Home } from "./screens/Home";
import { Releases } from "./screens/Releases";
import { ServiceIdentities } from "./screens/ServiceIdentities";
import { AccessPolicies } from "./screens/AccessPolicies";
import { Login, type LoginNotice } from "./screens/Login";

const TITLES: Record<string, string> = {
  "/": "Home",
  "/enterprise/graph": "Enterprise Graph",
  "/enterprise/systems": "Connected Systems",
  "/identity/entities": "Entities",
  "/identity/consent": "Consent",
  "/identity/authorization": "Authorization",
  "/identity/service-identities": "Service identities",
  "/identity/access-policies": "Access policies",
  "/intelligence/brains": "Brains",
  "/intelligence/memory": "Memory",
  "/intelligence/context-contracts": "Context Contracts",
  "/operations/events": "Event Backbone",
  "/operations/audit": "Audit Explorer",
  "/operations/releases": "Releases",
};

/**
 * A workspace hub, and the heading that names it.
 *
 * The key comes from the path rather than from a route per workspace, so
 * adding one to the registry does not also mean remembering to add a route —
 * `navigation.test.ts` checks that every workspace's `to` is served.
 */
function Hub() {
  const { workspaceKey } = useParams();
  return <WorkspaceHub workspaceKey={workspaceKey ?? ""} />;
}

/** What the shell should call the current page. */
function titleFor(pathname: string): string {
  const fixed = TITLES[pathname];
  if (fixed !== undefined) return fixed;
  if (pathname.startsWith("/workspace/")) {
    const key = pathname.slice("/workspace/".length);
    return workspaceByKey(key)?.label ?? "Nexus";
  }
  // A correlation view is still the Audit Explorer, whatever id is in the path.
  if (pathname.startsWith("/operations/audit/")) return "Audit Explorer";
  return "Nexus";
}

function NotFound() {
  return (
    <div className="nx-state">
      <p className="nx-state__title">There is no such page.</p>
      <p>
        The address may be from an older version of Nexus, or an area this deployment does not
        serve.
      </p>
    </div>
  );
}

function Shell() {
  const location = useLocation();
  return (
    <AppShell title={titleFor(location.pathname)}>
      <Routes>
        <Route path="/" element={<Home />} />
        {/* One entry for all six workspaces. A hub with nothing eligible
            answers honestly rather than 404-ing — the workspace is real, and
            unbuilt and unauthorized are different facts it distinguishes. */}
        <Route path="/workspace/:workspaceKey" element={<Hub />} />
        <Route path="/enterprise/graph" element={<EnterpriseGraph />} />
        <Route path="/enterprise/systems" element={<ConnectedSystems />} />
        <Route path="/identity/entities" element={<Entities />} />
        <Route path="/identity/consent" element={<Consent />} />
        <Route path="/identity/authorization" element={<Authorization />} />
        <Route path="/identity/service-identities" element={<ServiceIdentities />} />
        <Route path="/identity/access-policies" element={<AccessPolicies />} />
        <Route path="/intelligence/brains" element={<Brains />} />
        <Route path="/intelligence/memory" element={<Memory />} />
        <Route path="/intelligence/context-contracts" element={<ContextContracts />} />
        <Route path="/operations/events" element={<EventBackbone />} />
        <Route path="/operations/audit" element={<AuditExplorer />} />
        <Route path="/operations/audit/:correlationId" element={<AuditCorrelation />} />
        <Route path="/operations/releases" element={<Releases />} />
        <Route path="/index.html" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  );
}

/**
 * Which notice, if any, the query string is asking for.
 *
 * `signed_out=1` is produced by `signOut()`. `expired` (`LoginNotice` still
 * has it, and `Login` still renders it) has no producer here yet, and none is
 * added speculatively — see `navigation.ts` on why a planned capability gets
 * no seam until something real activates it. `AppShell`'s post-switch
 * `session.reload()` can still surface an expired session with no notice at
 * all, which is the known gap this leaves for part two: distinguish "expired"
 * from "first visit" — e.g. a `useRef` in `SessionProvider` recording whether
 * the probe ever resolved successfully — then wire a real `?error=`-style
 * handler here, deliberately and visibly, the way `navigation.ts` requires.
 */
export function noticeFor(params: URLSearchParams): LoginNotice | undefined {
  if (params.get("signed_out") === "1") return "signed-out";
  return undefined;
}

/** What to show while there is not yet a session, or there cannot be one.
 *
 * This renders the front door. It deliberately does not navigate: the previous
 * version called `signIn()` in this render body, which React may run more than
 * once, which a person could neither see nor interrupt, and which looped with
 * no exit when `/auth/login` or the provider misbehaved.
 */
function beforeSession(state: Resource<unknown>, reload: () => void) {
  if (state.kind === "loading") return <Loading label="Signing in" />;
  if (state.kind === "failed" || state.kind === "missing") {
    return <Failed error={state.error} onRetry={reload} />;
  }
  return <SignedOutDoor />;
}

/** The login screen, told where the person was heading and what to say. */
function SignedOutDoor() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const here = location.pathname + location.search;
  return (
    <Login
      returnPath={location.pathname === "/login" ? undefined : here}
      notice={noticeFor(params)}
    />
  );
}

export function App() {
  return (
    <SessionProvider
      fallback={(state, reload) => {
        if (state.kind === "failed" && state.error instanceof NexusError && state.error.isUnauthenticated) {
          return <SignedOutDoor />;
        }
        return beforeSession(state, reload);
      }}
    >
      <Shell />
    </SessionProvider>
  );
}
